import React from 'react';
import { IkigaiAnswers, INTERSECTION_LABELS } from './types';

interface IkigaiFlowerProps {
  answers: IkigaiAnswers;
  purposeSummary?: string;
}

/**
 * IkigaiFlower - Simple visual diagram showing the 4 Ikigai circles and their intersections.
 * Kept intentionally simple: 4 colored quadrants in a 2x2 grid with a center label.
 * Can be upgraded to an SVG Venn diagram later.
 */
const IkigaiFlower: React.FC<IkigaiFlowerProps> = ({ answers, purposeSummary }) => {
  const circles = [
    {
      key: 'love',
      label: 'Waar hou je van?',
      summary: answers.love_timeflies || answers.love_energy || answers.love_unpaid,
      color: '#FFF1F2',
      borderColor: '#FECDD3',
      textColor: '#BE123C',
      position: 'top-left',
    },
    {
      key: 'goodat',
      label: 'Waar ben je goed in?',
      summary: answers.goodat_help || answers.goodat_others || answers.goodat_learn,
      color: '#FFFBEB',
      borderColor: '#FDE68A',
      textColor: '#92400E',
      position: 'top-right',
    },
    {
      key: 'world',
      label: 'Wat heeft de wereld nodig?',
      summary: answers.world_problem || answers.world_change || answers.world_for,
      color: '#F0FDFA',
      borderColor: '#99F6E4',
      textColor: '#0F766E',
      position: 'bottom-left',
    },
    {
      key: 'paid',
      label: 'Waarvoor kun je betaald worden?',
      summary: answers.paid_earned || answers.paid_value || answers.paid_market,
      color: '#F5F3FF',
      borderColor: '#DDD6FE',
      textColor: '#6D28D9',
      position: 'bottom-right',
    },
  ];

  const intersections = [
    { label: INTERSECTION_LABELS.love_goodat.nl, desc: INTERSECTION_LABELS.love_goodat.description, top: '18%', left: '50%' },
    { label: INTERSECTION_LABELS.world_love.nl, desc: INTERSECTION_LABELS.world_love.description, top: '50%', left: '18%' },
    { label: INTERSECTION_LABELS.goodat_paid.nl, desc: INTERSECTION_LABELS.goodat_paid.description, top: '50%', left: '82%' },
    { label: INTERSECTION_LABELS.paid_world.nl, desc: INTERSECTION_LABELS.paid_world.description, top: '82%', left: '50%' },
  ];

  return (
    <div className="w-full">
      {/* Purpose summary banner */}
      {purposeSummary && (
        <div
          className="rounded-lg border p-4 mb-5 text-center"
          style={{ backgroundColor: 'rgba(122,148,136,0.08)', borderColor: 'rgba(122,148,136,0.3)' }}
        >
          <p className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: '#5E7E74' }}>
            Jouw Purpose
          </p>
          <p className="text-sm text-gray-800 leading-relaxed font-medium">
            {purposeSummary}
          </p>
        </div>
      )}

      {/* Diagram */}
      <div className="relative w-full" style={{ paddingBottom: '100%', maxWidth: 380, margin: '0 auto' }}>
        <div className="absolute inset-0">

          {/* Top-left: Waar hou je van? */}
          <div
            className="absolute rounded-tl-[40%] rounded-br-[20%] rounded-tr-[20%] rounded-bl-[20%] border p-4 flex flex-col justify-start"
            style={{
              top: 0, left: 0, width: '52%', height: '52%',
              backgroundColor: circles[0].color,
              borderColor: circles[0].borderColor,
            }}
          >
            <p className="text-xs font-semibold" style={{ color: circles[0].textColor }}>{circles[0].label}</p>
            <p className="text-xs text-gray-600 mt-1 line-clamp-3 leading-relaxed">{circles[0].summary}</p>
          </div>

          {/* Top-right: Waar ben je goed in? */}
          <div
            className="absolute rounded-tr-[40%] rounded-bl-[20%] rounded-tl-[20%] rounded-br-[20%] border p-4 flex flex-col justify-start items-end text-right"
            style={{
              top: 0, right: 0, width: '52%', height: '52%',
              backgroundColor: circles[1].color,
              borderColor: circles[1].borderColor,
            }}
          >
            <p className="text-xs font-semibold" style={{ color: circles[1].textColor }}>{circles[1].label}</p>
            <p className="text-xs text-gray-600 mt-1 line-clamp-3 leading-relaxed">{circles[1].summary}</p>
          </div>

          {/* Bottom-left: Wat heeft de wereld nodig? */}
          <div
            className="absolute rounded-bl-[40%] rounded-tr-[20%] rounded-tl-[20%] rounded-br-[20%] border p-4 flex flex-col justify-end"
            style={{
              bottom: 0, left: 0, width: '52%', height: '52%',
              backgroundColor: circles[2].color,
              borderColor: circles[2].borderColor,
            }}
          >
            <p className="text-xs font-semibold" style={{ color: circles[2].textColor }}>{circles[2].label}</p>
            <p className="text-xs text-gray-600 mt-1 line-clamp-3 leading-relaxed">{circles[2].summary}</p>
          </div>

          {/* Bottom-right: Waarvoor kun je betaald worden? */}
          <div
            className="absolute rounded-br-[40%] rounded-tl-[20%] rounded-tr-[20%] rounded-bl-[20%] border p-4 flex flex-col justify-end items-end text-right"
            style={{
              bottom: 0, right: 0, width: '52%', height: '52%',
              backgroundColor: circles[3].color,
              borderColor: circles[3].borderColor,
            }}
          >
            <p className="text-xs font-semibold" style={{ color: circles[3].textColor }}>{circles[3].label}</p>
            <p className="text-xs text-gray-600 mt-1 line-clamp-3 leading-relaxed">{circles[3].summary}</p>
          </div>

          {/* Center circle: Purpose */}
          <div
            className="absolute rounded-full flex flex-col items-center justify-center text-center border-2"
            style={{
              top: '50%', left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '26%', height: '26%',
              backgroundColor: '#FFFFFF',
              borderColor: '#7A9488',
              boxShadow: '0 2px 12px rgba(122,148,136,0.2)',
              zIndex: 10,
            }}
          >
            <p className="text-xs font-bold" style={{ color: '#3C4F49', fontSize: '0.65rem' }}>
              Purpose
            </p>
          </div>

          {/* Intersection labels */}
          {intersections.map(inter => (
            <div
              key={inter.label}
              className="absolute flex flex-col items-center justify-center text-center"
              style={{
                top: inter.top, left: inter.left,
                transform: 'translate(-50%, -50%)',
                zIndex: 5,
              }}
            >
              <span
                className="text-xs font-semibold px-1.5 py-0.5 rounded-full"
                style={{
                  backgroundColor: 'rgba(255,255,255,0.9)',
                  color: '#4B5563',
                  fontSize: '0.6rem',
                  border: '1px solid rgba(0,0,0,0.08)',
                  whiteSpace: 'nowrap',
                }}
              >
                {inter.label}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="mt-6 grid grid-cols-2 gap-2" style={{ maxWidth: 380, margin: '1.5rem auto 0' }}>
        {[
          { label: 'Passie', desc: 'Houden van + Goed in', color: '#BE123C' },
          { label: 'Professie', desc: 'Goed in + Betaald voor', color: '#92400E' },
          { label: 'Missie', desc: 'Houden van + Wereld nodig', color: '#0F766E' },
          { label: 'Roeping', desc: 'Betaald voor + Wereld nodig', color: '#6D28D9' },
        ].map(item => (
          <div key={item.label} className="flex items-start gap-2">
            <div className="w-2 h-2 rounded-full mt-1 flex-shrink-0" style={{ backgroundColor: item.color }} />
            <div>
              <p className="text-xs font-semibold text-gray-700">{item.label}</p>
              <p className="text-xs text-gray-400">{item.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default IkigaiFlower;
